import { createFetchAdapter, createHqApi } from '@hq/api-sdk';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { queryClient } from './queryClient';

export interface HqRuntimeConfig {
  apiBase: string;
  resBase: string;
  appTitle: string;
  uploadMaxMb: number;
  enableDebug: boolean;
}

export interface HqProfile {
  userId: string;
  /** 供应商以供应商主体作为 org 维度（role_type=SUPPLIER）。 */
  orgId: string;
  deptId: string;
  name: string;
  level: string;
  /** 后端下发的权限码；undefined 表示旧会话/开发态发号，不做前端菜单裁剪。 */
  perms?: string[];
  /** 个人账户：机构/科室名称、工号、头像、脱敏手机、邮箱。 */
  orgName?: string | null;
  deptName?: string | null;
  userCode?: string | null;
  avatarUrl?: string | null;
  mobile?: string | null;
  email?: string | null;
}

declare global {
  interface Window {
    __HQ_CONFIG__?: Partial<HqRuntimeConfig>;
  }
}

const fallback: HqRuntimeConfig = {
  apiBase: '/api',
  resBase: '/res',
  appTitle: '供应商协同门户',
  uploadMaxMb: 50,
  enableDebug: false
};

/** 本机 dev 强制同源，走 Vite 反代（5006→5001/5002）；生产绝对地址由 config.js 提供。 */
const devOverride: Partial<HqRuntimeConfig> = import.meta.env.DEV ? { apiBase: '/api', resBase: '/res' } : {};

const runtime: HqRuntimeConfig = { ...fallback, ...(window.__HQ_CONFIG__ ?? {}), ...devOverride };

export const config: HqRuntimeConfig = runtime;

export const appTitle = config.appTitle;

interface AuthState {
  token: string | null;
  profile: HqProfile | null;
  setSession: (token: string, profile: HqProfile) => void;
  /** 会话局部更新（头像/科室等由 /auth/me 补全）。 */
  patchProfile: (patch: Partial<HqProfile>) => void;
  clear: () => void;
}

/**
 * 供应商门户与内部系统**不共享登录态**（docs/05 §1）：
 * 只写 sessionStorage 且 key 独立，生产环境还应配合独立域名下的 Cookie/存储隔离。
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      profile: null,
      setSession: (token, profile) => set({ token, profile }),
      patchProfile: (patch) =>
        set((state) => ({ profile: state.profile ? { ...state.profile, ...patch } : null })),
      clear: () => set({ token: null, profile: null })
    }),
    {
      name: 'hq-supplier-auth',
      storage: createJSONStorage(() => sessionStorage)
    }
  )
);

const getToken = (): string | null => useAuthStore.getState().token;

export const hq = createHqApi({
  adapter: createFetchAdapter(),
  baseUrl: config.apiBase,
  resBaseUrl: config.resBase,
  resolveToken: getToken,
  // 401 时登录态与查询缓存一起清：供应商门户换账号后不能看到上一家的残留数据
  onUnauthorized: () => {
    useAuthStore.getState().clear();
    queryClient.clear();
  }
});
